import React, { useMemo, useState } from "react";
import { scaleLinear } from "d3-scale";
import {
	Figure, SURFACE, Tooltip, TooltipRow, XAxis, YGrid, formatNumber, seriesColor, useWidth,
} from "./shared";

const HEIGHT = 340;
const MARGIN = { top: 28, right: 130, bottom: 50, left: 48 };
const HIT_RADIUS = 24;

// Grouped scatter with an OLS fit line per group. Props (from Rails):
//   number, title, note, xLabel, yLabel, xDomain, yDomain, xTicks, yTicks,
//   groups: [{ name, fit: { slope, intercept, r }, points: [{ x, y }] }]
export default function ScatterPlot({ number, title, note, xLabel, yLabel, xDomain, yDomain, xTicks, yTicks, groups }) {
	const [ref, width] = useWidth();
	const [active, setActive] = useState(null);

	const narrow = width < 560;
	const margin = { ...MARGIN, right: narrow ? 16 : MARGIN.right };
	const innerW = width - margin.left - margin.right;
	const innerH = HEIGHT - margin.top - margin.bottom;

	const x = scaleLinear().domain(xDomain).range([margin.left, margin.left + innerW]);
	const y = scaleLinear().domain(yDomain).range([margin.top + innerH, margin.top]);

	// Every point, flattened and ordered by x for keyboard stepping.
	const points = useMemo(
		() => groups
			.flatMap((g, gi) => g.points.map((p) => ({ ...p, group: g.name, gi })))
			.sort((a, b) => a.x - b.x),
		[groups],
	);

	function nearest(event) {
		const rect = event.currentTarget.getBoundingClientRect();
		const px = event.clientX - rect.left;
		const py = event.clientY - rect.top;
		let best = null;
		let bestDist = HIT_RADIUS;
		points.forEach((p, i) => {
			const d = Math.hypot(x(p.x) - px, y(p.y) - py);
			if (d < bestDist) {
				best = i;
				bestDist = d;
			}
		});
		return best;
	}

	function onKeyDown(event) {
		if (event.key === "ArrowRight") setActive((a) => Math.min(points.length - 1, (a ?? -1) + 1));
		else if (event.key === "ArrowLeft") setActive((a) => Math.max(0, (a ?? points.length) - 1));
		else if (event.key === "Escape") setActive(null);
		else return;
		event.preventDefault();
	}

	const fitLine = (g) => {
		const xsIn = g.points.map((p) => p.x);
		const x0 = Math.min(...xsIn);
		const x1 = Math.max(...xsIn);
		return { x0, x1, y0: g.fit.intercept + g.fit.slope * x0, y1: g.fit.intercept + g.fit.slope * x1 };
	};

	const table = {
		columns: ["Group", xLabel, yLabel],
		rows: points.map((p) => [p.group, formatNumber(p.x, 0), formatNumber(p.y, 1)]),
	};

	const hovered = active !== null ? points[active] : null;

	return (
		<Figure
			number={number}
			title={title}
			note={note}
			table={table}
			legend={groups.map((g, i) => ({ name: g.name, color: seriesColor(i), shape: "dot" }))}
		>
			<div ref={ref} className="relative">
				<svg
					width={width}
					height={HEIGHT}
					role="group"
					aria-label={`Figure ${number}: ${title}. Use left and right arrow keys to step through points.`}
					tabIndex={0}
					className="block overflow-visible font-sans focus-visible:outline-offset-4"
					onPointerMove={(e) => setActive(nearest(e))}
					onPointerLeave={() => setActive(null)}
					onKeyDown={onKeyDown}
					onBlur={() => setActive(null)}
				>
					<text x={margin.left - 40} y={12} fontSize="12.5" fill="var(--color-gray-700)">{yLabel}</text>
					<YGrid scale={y} ticks={yTicks} width={innerW} left={margin.left} />
					<XAxis scale={x} ticks={xTicks} y={margin.top + innerH} label={xLabel} width={innerW} left={margin.left} />

					{groups.map((g, gi) =>
						g.points.map((p, k) => (
							<circle
								key={`${g.name}-${k}`}
								cx={x(p.x)} cy={y(p.y)} r="4"
								fill={seriesColor(gi)} fillOpacity="0.85"
								stroke={SURFACE} strokeWidth="2"
							/>
						)),
					)}

					{groups.map((g, gi) => {
						const f = fitLine(g);
						return (
							<line
								key={`fit-${g.name}`}
								x1={x(f.x0)} y1={y(f.y0)} x2={x(f.x1)} y2={y(f.y1)}
								stroke={seriesColor(gi)} strokeWidth="2" strokeLinecap="round"
							/>
						);
					})}

					{!narrow && groups.map((g) => {
						const f = fitLine(g);
						return (
							<text key={`label-${g.name}`} x={x(f.x1) + 10} y={y(f.y1)} dy="0.32em" fontSize="12.5" fill="var(--color-gray-700)">
								<tspan>{g.name}</tspan>
								<tspan x={x(f.x1) + 10} dy="1.3em" fill="var(--color-gray-600)" className="dp-figures">
									{`r = ${formatNumber(g.fit.r, 2)}`}
								</tspan>
							</text>
						);
					})}

					{hovered && (
						<circle
							cx={x(hovered.x)} cy={y(hovered.y)} r="7"
							fill="none" stroke="var(--color-ink)" strokeWidth="1.5"
						/>
					)}
				</svg>

				{hovered && (
					<Tooltip x={x(hovered.x)} y={y(hovered.y) - 20} containerWidth={width}>
						<TooltipRow color={seriesColor(hovered.gi)} shape="dot" label={hovered.group} value="" />
						<div className="dp-figures text-ink">
							<span className="font-semibold">{formatNumber(hovered.x, 0)}</span>
							<span className="text-gray-600"> {xLabel.toLowerCase()}</span>
						</div>
						<div className="dp-figures text-ink">
							<span className="font-semibold">{formatNumber(hovered.y, 1)}</span>
							<span className="text-gray-600"> {yLabel.toLowerCase()}</span>
						</div>
					</Tooltip>
				)}
			</div>
		</Figure>
	);
}
